import { useState, useEffect, useRef } from 'react'
import { useTranslation } from '../../i18n/LanguageContext'
import { useTimer } from '../useTimer'
import { useSudoku, type SudokuMode, type Difficulty } from './useSudoku'
import GameSetupScreen from '../GameSetupScreen'
import SaveErrorBanner from '../SaveErrorBanner'
import { ss } from '../gameSetupStyles'
import GamePlayHeader from '../GamePlayHeader'
import { useSessionMode } from '../../professional/SessionModeContext'
import { useGameCompletion } from '../../hooks/useGameCompletion'
import { useGameSession } from '../../hooks/useGameSession'
import { useActivitySession } from '../../hooks/useActivitySession'
import GameCompleteScreen from '../GameCompleteScreen'
import { formatGameTime } from '../utils'
import { COLORS } from '../../styles/tokens'

interface SudokuConfig { mode: SudokuMode; difficulty: Difficulty }

interface SudokuGameProps {
  onBack: () => void
}

type GameScreen = 'setup' | 'playing' | 'complete'

const MODES: SudokuMode[]      = ['standard', 'mini']
const DIFFICULTIES: Difficulty[] = ['easy', 'medium', 'hard']

export default function SudokuGame({ onBack }: SudokuGameProps) {
  const sessionMode = useSessionMode()
  const { savedSession, saveConfig, clear } = useActivitySession<SudokuConfig>('sudoku', !sessionMode)
  const restored = savedSession?.config ?? null

  const [screen,     setScreen]     = useState<GameScreen>(sessionMode ? 'playing' : (restored ? 'playing' : 'setup'))
  const [mode,       setMode]       = useState<SudokuMode>((sessionMode?.config.mode as SudokuMode) ?? restored?.mode ?? 'standard')
  const [difficulty, setDifficulty] = useState<Difficulty>((sessionMode?.config.difficulty as Difficulty) ?? restored?.difficulty ?? 'easy')

  const { state, resetGame, selectCell, enterNumber, clearCell, toggleNotesMode } = useSudoku(mode, difficulty)
  const { gameComplete, moves, startTime, endTime, correct, totalEmpty } = state

  const { sessionId } = useGameSession({
    activitySlug: 'sudoku',
    difficulty,
    mode,
    enabled: screen === 'playing',
  })

  const { saveError, clearSaveError } = useGameCompletion({
    gameComplete,
    activitySlug: 'sudoku',
    difficulty,
    mode,
    moves,
    startTime,
    endTime,
    pairsTotal:  totalEmpty,
    pairsMatched: correct,
    sessionId,
    onCompleted: () => { clear(); setScreen('complete') },
  })

  const handleStart = () => { resetGame(); saveConfig({ mode, difficulty }); setScreen('playing') }

  const { t } = useTranslation()

  return (
    <div style={s.root}>
      {saveError && <SaveErrorBanner message={t.common.saveError} dismissLabel={t.common.dismissError} onDismiss={clearSaveError} />}
      {screen === 'setup' && (
        <SetupScreen
          mode={mode}
          difficulty={difficulty}
          onModeChange={setMode}
          onDifficultyChange={setDifficulty}
          onStart={handleStart}
          onBack={onBack}
        />
      )}
      {screen === 'playing' && (
        <PlayScreen
          state={state}
          onSelectCell={selectCell}
          onEnterNumber={enterNumber}
          onClear={clearCell}
          onToggleNotes={toggleNotesMode}
          onBack={sessionMode ? onBack : () => { clear(); setScreen('setup') }}
        />
      )}
      {screen === 'complete' && (
        <GameCompleteScreen
          emoji="🎉"
          title={t.sudoku.complete.title}
          subtitle={t.sudoku.complete.subtitle}
          stats={[
            { value: String(state.moves), label: t.sudoku.complete.movesLabel },
            { value: String(state.errors), label: t.sudoku.complete.errorsLabel },
            { value: formatGameTime(state.startTime, state.endTime), label: t.sudoku.complete.timeLabel },
            { value: t.sudoku.difficulty[difficulty].label, label: t.sudoku.complete.levelLabel },
          ]}
          playAgainLabel={t.sudoku.complete.playAgain}
          changeLevelLabel={t.sudoku.complete.changeLevel}
          backLabel={t.sudoku.complete.backToDashboard}
          onPlayAgain={() => { resetGame(); saveConfig({ mode, difficulty }); setScreen('playing') }}
          onChangeLevel={() => { clear(); setScreen('setup') }}
          onBack={onBack}
        />
      )}
    </div>
  )
}

/* ── Setup Screen ─────────────────────────────────────────────── */

interface SetupScreenProps {
  mode: SudokuMode
  difficulty: Difficulty
  onModeChange: (m: SudokuMode) => void
  onDifficultyChange: (d: Difficulty) => void
  onStart: () => void
  onBack: () => void
}

function SetupScreen({ mode, difficulty, onModeChange, onDifficultyChange, onStart, onBack }: SetupScreenProps) {
  const { t } = useTranslation()
  const sdk = t.sudoku

  return (
    <GameSetupScreen
      title={sdk.title}
      subtitle={sdk.subtitle}
      backLabel={sdk.backToDashboard}
      onBack={onBack}
      difficulties={DIFFICULTIES.map((id) => ({ id, ...sdk.difficulty[id] }))}
      selectedDifficulty={difficulty}
      onDifficultyChange={(id) => onDifficultyChange(id as Difficulty)}
      chooseDifficultyLabel={sdk.chooseDifficultyTitle}
      startLabel={sdk.startBtn}
      onStart={onStart}
      preview={<SudokuPreview mode={mode} />}
    >
      {/* Mode selection */}
      <section>
        <h2 style={ss.optionLabel}>{sdk.chooseModeTitle}</h2>
        <div style={ss.modeGrid}>
          {MODES.map((id) => {
            const cfg = sdk.modes[id]
            return (
              <button
                key={id}
                onClick={() => onModeChange(id)}
                style={{ ...ss.modeCard, ...(mode === id ? ss.modeCardActive : {}) }}
                aria-pressed={mode === id}
              >
                <span style={s.modeIcon}>{id === 'standard' ? '9×9' : '4×4'}</span>
                <span style={s.modeLabel}>{cfg.label}</span>
                <span style={s.modeDesc}>{cfg.description}</span>
              </button>
            )
          })}
        </div>
      </section>
    </GameSetupScreen>
  )
}

/* ── Mini preview ──────────────────────────────────────────────── */
function SudokuPreview({ mode }: { mode: SudokuMode }) {
  const size    = mode === 'standard' ? 9 : 4
  const boxSize = mode === 'standard' ? 3 : 2
  const SAMPLE_9: number[] = [
    5,3,0, 0,7,0, 0,0,0,
    6,0,0, 1,9,5, 0,0,0,
    0,9,8, 0,0,0, 0,6,0,
    8,0,0, 0,6,0, 0,0,3,
    4,0,0, 8,0,3, 0,0,1,
    7,0,0, 0,2,0, 0,0,6,
    0,6,0, 0,0,0, 2,8,0,
    0,0,0, 4,1,9, 0,0,5,
    0,0,0, 0,8,0, 0,7,9,
  ]
  const SAMPLE_4: number[] = [
    1,0,0,4,
    0,4,1,0,
    0,1,4,0,
    4,0,0,1,
  ]
  const cells = mode === 'standard' ? SAMPLE_9 : SAMPLE_4
  const cellPx = mode === 'standard' ? 28 : 56

  return (
    <div style={{
      display:       'grid',
      gridTemplateColumns: `repeat(${size}, ${cellPx}px)`,
      border:        `2px solid ${COLORS.textBody}`,
      borderRadius:  8,
      overflow:      'hidden',
      width:         'fit-content',
      margin:        '0 auto',
    }}>
      {cells.map((val, i) => {
        const r = Math.floor(i / size)
        const c = i % size
        const thick = `2px solid ${COLORS.textBody}`
        const thin  = '1px solid #C8BEB4'
        return (
          <div
            key={i}
            style={{
              width:       cellPx,
              height:      cellPx,
              display:     'flex',
              alignItems:  'center',
              justifyContent: 'center',
              fontSize:    mode === 'standard' ? 11 : 20,
              fontWeight:  val !== 0 ? 700 : 400,
              color:       val !== 0 ? COLORS.greenDark : 'transparent',
              background:  COLORS.bgPage,
              borderTop:   r % boxSize === 0 ? thick : thin,
              borderLeft:  c % boxSize === 0 ? thick : thin,
              borderBottom:(r + 1) % boxSize === 0 ? thick : thin,
              borderRight: (c + 1) % boxSize === 0 ? thick : thin,
            }}
          >
            {val !== 0 ? val : '·'}
          </div>
        )
      })}
    </div>
  )
}

/* ── Play Screen ──────────────────────────────────────────────── */

interface PlayScreenProps {
  state: ReturnType<typeof useSudoku>['state']
  onSelectCell: (r: number, c: number) => void
  onEnterNumber: (n: number) => void
  onClear: () => void
  onToggleNotes: () => void
  onBack: () => void
}

function PlayScreen({ state, onSelectCell, onEnterNumber, onClear, onToggleNotes, onBack }: PlayScreenProps) {
  const { t } = useTranslation()
  const sdk        = t.sudoku
  const timeDisplay = useTimer(state.startTime, state.endTime)
  const progress   = state.totalEmpty > 0 ? (state.correct / state.totalEmpty) * 100 : 0

  const instruction = state.selected
    ? sdk.instructionEnter
    : sdk.instructionSelect

  const bodyRef = useRef<HTMLDivElement>(null)
  const [bodySize, setBodySize] = useState({ width: 0, height: 0 })
  useEffect(() => {
    const el = bodyRef.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      setBodySize({
        width:  Math.floor(width),
        height: Math.floor(height),
      })
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const isLandscape = bodySize.width > bodySize.height && bodySize.height > 0

  const BOARD_PAD        = 24
  const RIGHT_PANEL_W    = 280
  const INSTRUCTION_H    = 44
  const NUMPAD_H         = state.size === 9 ? 220 : 142
  const fallbackGridSize = state.size === 9 ? 324 : 240

  let gridSize = fallbackGridSize
  if (bodySize.width > 0 && bodySize.height > 0) {
    if (isLandscape) {
      const availableW = bodySize.width - RIGHT_PANEL_W - BOARD_PAD * 2
      const availableH = bodySize.height - BOARD_PAD * 2
      gridSize = Math.floor(Math.min(availableW, availableH))
    } else {
      const availableW = bodySize.width - BOARD_PAD * 2
      const availableH = bodySize.height - INSTRUCTION_H - NUMPAD_H - BOARD_PAD * 2
      gridSize = Math.floor(Math.min(availableW, availableH))
    }
  }
  gridSize = Math.max(0, gridSize)

  const numpad = (
    <NumberPad
      size={state.size}
      notesMode={state.notesMode}
      onNumber={onEnterNumber}
      onClear={onClear}
      onToggleNotes={onToggleNotes}
      sdk={sdk}
    />
  )

  return (
    <div style={s.playRoot}>
      <GamePlayHeader
        title={sdk.title}
        stats={[
          { value: state.errors,  label: sdk.errors },
          { value: timeDisplay,   label: sdk.time },
        ]}
        progress={progress}
        progressNow={state.correct}
        progressMax={state.totalEmpty}
        onBack={onBack}
        backLabel={sdk.backToDashboard}
      />

      <div ref={bodyRef} style={{ ...s.playBody, ...(isLandscape ? s.landscapeBody : s.portraitBody) }}>
        {isLandscape ? (
          /* Landscape: grid left, numpad+instruction right */
          <>
            <div style={s.landscapeBoardWrap}>
              <SudokuGrid state={state} onSelectCell={onSelectCell} gridSizePx={gridSize > 0 ? gridSize : undefined} />
            </div>
            <div style={s.landscapeRight}>
              <p style={s.instruction}><em>{instruction}</em></p>
              {numpad}
            </div>
          </>
        ) : (
          /* Portrait: grid top, instruction + numpad below */
          <>
            <div style={s.boardWrap}>
              <SudokuGrid state={state} onSelectCell={onSelectCell} gridSizePx={gridSize > 0 ? gridSize : undefined} />
            </div>
            <p style={s.instruction}><em>{instruction}</em></p>
            {numpad}
          </>
        )}
      </div>
    </div>
  )
}

/* ── Sudoku Grid ──────────────────────────────────────────────── */

interface SudokuGridProps {
  state: ReturnType<typeof useSudoku>['state']
  onSelectCell: (r: number, c: number) => void
  gridSizePx?: number
}

function SudokuGrid({ state, onSelectCell, gridSizePx }: SudokuGridProps) {
  const { size, boxSize, grid, given, conflicts, notes, selected, solution } = state

  const selR = selected ? selected[0] : -1
  const selC = selected ? selected[1] : -1

  // Cells in the same box as selected
  const selBoxR = selR >= 0 ? Math.floor(selR / boxSize) * boxSize : -1
  const selBoxC = selC >= 0 ? Math.floor(selC / boxSize) * boxSize : -1

  // Same-number highlight: the value in selected cell
  const selVal = (selR >= 0 && selC >= 0) ? grid[selR][selC] : 0

  const thick = `2.5px solid ${COLORS.textBody}`
  const thin  = '1px solid #C8BEB4'

  const totalPx = gridSizePx ?? (size === 9 ? 324 : 240)

  return (
    <div style={{
      display:       'grid',
      gridTemplateColumns: `repeat(${size}, 1fr)`,
      width:         totalPx,
      maxWidth:      '100%',
      aspectRatio:   '1',
    }}>
      {Array.from({ length: size }, (_, r) =>
        Array.from({ length: size }, (_, c) => {
          const val         = grid[r][c]
          const isGiven     = given[r][c]
          const isConflict  = conflicts[r][c]
          const isSelected  = r === selR && c === selC
          const isSameRow   = r === selR && selR >= 0
          const isSameCol   = c === selC && selC >= 0
          const isSameBox   = selR >= 0 && r >= selBoxR && r < selBoxR + boxSize && c >= selBoxC && c < selBoxC + boxSize
          const isSameNum   = selVal !== 0 && val === selVal && !isSelected
          const cellNotes   = notes[r * size + c]
          const hasNotes    = cellNotes.some(Boolean)
          const isCorrect   = !isGiven && val !== 0 && val === solution[r][c]

          let bg: string = COLORS.bgPage
          if (isSelected)         bg = COLORS.greenSurface
          else if (isSameNum)     bg = '#E8F5F0'
          else if (isSameRow || isSameCol || isSameBox) bg = '#F0F8F4'

          let color: string = COLORS.greenDark
          if (isGiven)         color = COLORS.textDark
          else if (isConflict) color = COLORS.errorText
          else if (isCorrect)  color = COLORS.green
          else                 color = '#1D6FA4'

          return (
            <button
              key={`${r}-${c}`}
              onClick={() => onSelectCell(r, c)}
              style={{
                position:       'relative',
                display:        'flex',
                alignItems:     'center',
                justifyContent: 'center',
                background:     bg,
                borderTop:    r % boxSize === 0        ? thick : thin,
                borderLeft:   c % boxSize === 0        ? thick : thin,
                borderBottom: (r + 1) % boxSize === 0  ? thick : thin,
                borderRight:  (c + 1) % boxSize === 0  ? thick : thin,
                cursor:         isGiven ? 'default' : 'pointer',
                fontSize:       Math.round(totalPx / size * (hasNotes ? 0.22 : 0.50)),
                fontWeight:     isGiven ? 800 : 600,
                fontFamily:     'Nunito, sans-serif',
                color,
                transition:     'background 0.1s ease',
                aspectRatio:    '1',
              }}
              aria-label={`Row ${r + 1} column ${c + 1}: ${val !== 0 ? val : 'empty'}`}
              aria-pressed={isSelected}
            >
              {val !== 0 && !hasNotes && val}
              {val === 0 && hasNotes && (
                <NotesGrid cellNotes={cellNotes} size={size} boxSize={boxSize} totalPx={totalPx} />
              )}
            </button>
          )
        }),
      )}
    </div>
  )
}

function NotesGrid({ cellNotes, size, boxSize, totalPx }: { cellNotes: boolean[]; size: number; boxSize: number; totalPx: number }) {
  return (
    <div style={{
      display:       'grid',
      gridTemplateColumns: `repeat(${boxSize}, 1fr)`,
      width:         '100%',
      height:        '100%',
      padding:       1,
      gap:           0,
    }}>
      {Array.from({ length: size }, (_, i) => (
        <span
          key={i}
          style={{
            fontSize:       Math.round(totalPx / size * 0.19),
            fontFamily:     'Nunito, sans-serif',
            fontWeight:     700,
            color:          cellNotes[i] ? '#1D6FA4' : 'transparent',
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
            lineHeight:     1,
          }}
        >
          {i + 1}
        </span>
      ))}
    </div>
  )
}

/* ── Number Pad ───────────────────────────────────────────────── */

interface NumberPadProps {
  size:          number
  notesMode:     boolean
  onNumber:      (n: number) => void
  onClear:       () => void
  onToggleNotes: () => void
  sdk:           ReturnType<typeof useTranslation>['t']['sudoku']
}

function NumberPad({ size, notesMode, onNumber, onClear, onToggleNotes, sdk }: NumberPadProps) {
  const numbers = Array.from({ length: size }, (_, i) => i + 1)
  // For 9x9: layout 3 rows of 3 numbers to mirror the box structure
  // For 4x4: 1 row of 4 numbers
  const cols = size === 9 ? 3 : 4

  return (
    <div style={s.padRoot}>
      <div style={{ ...s.padGrid, gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
        {numbers.map(n => (
          <button
            key={n}
            onClick={() => onNumber(n)}
            style={s.padNum}
            aria-label={`${n}`}
          >
            {n}
          </button>
        ))}
      </div>
      <div style={s.padActions}>
        <button
          onClick={onToggleNotes}
          style={{ ...s.padActionBtn, ...(notesMode ? s.padActionBtnActive : {}) }}
          aria-pressed={notesMode}
          aria-label={sdk.notes}
        >
          ✏️ {sdk.notes}
        </button>
        <button
          onClick={onClear}
          style={s.padActionBtn}
          aria-label={sdk.erase}
        >
          ⌫ {sdk.erase}
        </button>
      </div>
    </div>
  )
}

/* ── Styles ───────────────────────────────────────────────────── */
const s: Record<string, React.CSSProperties> = {
  root: { minHeight: '100vh' },

  modeIcon:  { fontSize: '1.4rem', fontWeight: 800, color: COLORS.greenDark, fontFamily: 'Nunito, sans-serif' },
  modeLabel: { fontSize: '1rem',   fontWeight: 700, color: COLORS.textDark },
  modeDesc:  { fontSize: '0.875rem', color: COLORS.textBody, fontWeight: 500, lineHeight: 1.4 },

  /* Play */
  playRoot: { height: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  playBody: {
    flex:      1,
    minHeight: 0,
    overflow:  'hidden',
  },
  portraitBody: {
    display:       'flex',
    flexDirection: 'column',
  },
  boardWrap: {
    flex:           1,
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    padding:        12,
    overflow:       'hidden',
    minHeight:      0,
  },

  /* Landscape layout */
  landscapeBody: {
    display:        'flex',
    flexDirection:  'row',
  },
  landscapeBoardWrap: {
    flex:           1,
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    overflow:       'hidden',
    padding:        12,
    minWidth:       0,
  },
  landscapeRight: {
    display:        'flex',
    flexDirection:  'column',
    justifyContent: 'center',
    overflowY:      'auto',
    flexShrink:     0,
    width:          280,
    borderLeft:     '1px solid rgba(27,67,50,0.1)',
  },

  instruction: {
    fontSize:    '0.95rem',
    color:       COLORS.textBody,
    textAlign:   'center',
    padding:     '4px 16px 8px',
    margin:      0,
    flexShrink:  0,
  },

  /* Number pad */
  padRoot: {
    padding:    '8px 12px 24px',
    display:    'flex',
    flexDirection: 'column',
    gap:        10,
    flexShrink: 0,
    maxWidth:   400,
    width:      '100%',
    margin:     '0 auto',
  },
  padGrid: {
    display:    'grid',
    gap:        8,
  },
  padNum: {
    fontSize:       '1.4rem',
    fontWeight:     800,
    fontFamily:     'Nunito, sans-serif',
    color:          COLORS.greenDark,
    background:     COLORS.bgCard,
    border:         '1.5px solid #D8C9B8',
    borderRadius:   12,
    minHeight:      52,
    cursor:         'pointer',
    boxShadow:      '0 2px 6px rgba(27,67,50,0.08)',
    transition:     'all 0.1s ease',
  },
  padActions: {
    display:             'grid',
    gridTemplateColumns: '1fr 1fr',
    gap:                 8,
  },
  padActionBtn: {
    fontSize:     '0.9rem',
    fontWeight:   700,
    color:        COLORS.textBody,
    background:   '#F5F0E8',
    border:       '1.5px solid #D8C9B8',
    borderRadius: 12,
    minHeight:    48,
    cursor:       'pointer',
    transition:   'all 0.1s ease',
  },
  padActionBtnActive: {
    background: '#D1EDE4',
    border:     `1.5px solid ${COLORS.green}`,
    color:      COLORS.greenDark,
  },

}
